/**
 * Rock BTS — field metadata, options, and defaults (data only, no JSX).
 * Domain values trace to
 * qa/moftaki-lab-rock-test-requirements-recording-1-of-1-2026-06-11/specs/BTS-CALC-SPEC.md
 * and BTS-FAILURE-DESCRIPTION-SPEC.md (workbook cell citations there).
 */
import type { RockBTSGeneral, RockBTSSpecimen } from '../../../types/rockBts';

export const SPECIMEN_COUNT_OPTIONS = [
  { value: 1, label: '1 Specimen' },
  { value: 2, label: '2 Specimens' },
] as const;

/** Workbook AD26–30; default 'As received' (REQ-01 / D7). */
export const BTS_CONDITION_OPTIONS = [
  'As received',
  'Saturated in laboratory',
  'Air dried in laboratory',
  'Oven dried in laboratory',
  'Other',
] as const;

/** Plane-of-weakness checkboxes (REQ-02). 'None' is exclusive of the rest. */
export const BTS_POW_OPTIONS = [
  { field: 'powNone', label: 'None' },
  { field: 'powBedding', label: 'Bedding' },
  { field: 'powFoliation', label: 'Foliation' },
  { field: 'powDiscontinuities', label: 'Discontinuities' },
  { field: 'powVeins', label: 'Veins' },
  { field: 'powOther', label: 'Other' },
] as const;

/** Workbook B71–B78 / AK64–71. */
export const BTS_FAILURE_TYPE_OPTIONS = [
  'Layer Activation',
  'Non-Central',
  'Non-Central & Layer Activation',
  'Central',
  'Central & Layer Activation',
  'Multiple',
  'Multiple & Layer Activation',
  'Invalid',
] as const;

/** Failure types whose sentence consumes the secondary failure row. */
export const BTS_FAILURE_TYPES_WITH_SECONDARY = new Set<string>([
  'Non-Central & Layer Activation',
  'Central & Layer Activation',
  'Multiple & Layer Activation',
]);

export const BTS_FRACTURE_MODE_OPTIONS = ['tensile', 'shear', 'mixed-mode'] as const;

export const BTS_PLANE_TYPE_OPTIONS = [
  'vein',
  'microdefect',
  'rock matrix',
  'bedding',
  'intrusion',
  'foliation',
] as const;

/** ASTM D3967 stress-rate band (MPa/s) — displayed on the guidance card. */
export const BTS_MIN_STRESS_RATE_MPAS = 0.05;
export const BTS_MAX_STRESS_RATE_MPAS = 0.35;

/** Operator helper notes beside the deviation helper (workbook AB43/AB44). */
export const BTS_NOTE_1 =
  'Note 1: When a central fracture parallel to the plane of weakness is induced, this is classified as layer activation and not as central.';
export const BTS_NOTE_2 =
  'Note 2: If a fracture deviates beyond 10% of the diameter on both sides of the central line, this is classified as non-central.';

export const EMPTY_ROCK_BTS_GENERAL: RockBTSGeneral = {
  specimenCount: 2, // BGC standard (REQ-03)
  testDate: null,
  testedBy: null,
  remarksText: null,
  additionalObservations: null,
};

export const emptyBtsSpecimen = (specimenNumber: number): RockBTSSpecimen => ({
  specimenNumber,
  condition: 'As received',
  conditionOther: null,
  conditionRemarks: null,
  powNone: null,
  powBedding: null,
  powFoliation: null,
  powDiscontinuities: null,
  powVeins: null,
  powOther: null,
  powOtherText: null,
  powAngleADeg: null,
  powAngleBDeg: null,
  diameter1: null,
  diameter2: null,
  diameter3: null,
  thickness1: null,
  thickness2: null,
  thickness3: null,
  massG: null,
  loadingRateKNs: null,
  maxLoadKN: null,
  deviationXFrontMm: null,
  deviationXBackMm: null,
  failureType: null,
  primaryFractureMode: null,
  primaryPlaneType: null,
  primaryAngleADeg: null,
  primaryAngleBDeg: null,
  secondaryFractureMode: null,
  secondaryPlaneType: null,
  secondaryAngleADeg: null,
  secondaryAngleBDeg: null,
  failureDescriptionOverride: null,
  failureRemarks: null,
});
